import { cn } from '@/lib/utils'
import { curveMonotoneX, extent, line, max, scaleLinear, scaleTime, timeMonth } from 'd3'
import { format } from 'date-fns'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'

/** A single point on the trend: a value measured at a point in time. */
export interface MetricPoint {
  date: Date
  value: number
}

interface MetricTrendChartProps {
  /** Points to plot. The chart renders nothing useful with fewer than two. */
  data: MetricPoint[]
  /** Heading shown above the chart, e.g. "Weight progress over time". */
  title?: string
  /** Rotated label drawn alongside the Y axis, e.g. "Weight (KG)". */
  yAxisLabel: string
  /** Unit appended to values in the hover tooltip, e.g. "kg". */
  unit?: string
  /** Stroke colour of the line and markers. */
  color?: string
  /** Plot height in pixels (excludes the title). */
  height?: number
  className?: string
}

const MARGIN = { top: 16, right: 24, bottom: 40, left: 52 }
const DAY_MS = 24 * 60 * 60 * 1000
const TOOLTIP_HALF_WIDTH = 72

/** A "nice" Y-axis step (1/2/5 × 10ⁿ) giving roughly three gridlines up to `maxValue`. */
function niceStep(maxValue: number): number {
  if (maxValue <= 0) return 1
  const rough = maxValue / 3
  const magnitude = 10 ** Math.floor(Math.log10(rough))
  const normalized = rough / magnitude
  const nice = normalized < 1.5 ? 1 : normalized < 3 ? 2 : normalized < 7 ? 5 : 10
  return nice * magnitude
}

export default function MetricTrendChart({
  data,
  title,
  yAxisLabel,
  unit = '',
  color = '#24272b',
  height = 320,
  className
}: MetricTrendChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(640)
  const [hovered, setHovered] = useState<number | null>(null)

  useLayoutEffect(() => {
    const element = containerRef.current
    if (!element) return
    // Measure synchronously before paint to avoid a first-frame flash at the default width.
    setWidth(element.getBoundingClientRect().width || 640)
    const observer = new ResizeObserver(entries => {
      const next = entries[0]?.contentRect.width
      if (next) setWidth(next)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  // Stable across width-only re-renders, so a `hovered` index stays valid (and a stale
  // index is harmless — `active` below guards out-of-bounds).
  const points = useMemo(() => [...data].sort((a, b) => a.date.getTime() - b.date.getTime()), [data])

  const formatValue = (value: number) => `${value}${unit ? ` ${unit}` : ''}`

  if (points.length < 2) return null

  // X scale — pad a flat (single-day) span so the line is never degenerate.
  const [minDate, maxDate] = extent(points, p => p.date) as [Date, Date]
  const sameDay = minDate.getTime() === maxDate.getTime()
  const domainStart = sameDay ? new Date(minDate.getTime() - DAY_MS) : minDate
  const domainEnd = sameDay ? new Date(maxDate.getTime() + DAY_MS) : maxDate
  const xScale = scaleTime()
    .domain([domainStart, domainEnd])
    .range([MARGIN.left, width - MARGIN.right])

  // Y scale — anchored at zero with a rounded top, matching the design's 0/50/100/150.
  const dataMax = max(points, p => p.value) ?? 0
  const step = niceStep(dataMax)
  const yMax = Math.max(step, Math.ceil(dataMax / step) * step)
  const yScale = scaleLinear()
    .domain([0, yMax])
    .range([height - MARGIN.bottom, MARGIN.top])
  const yTicks: number[] = []
  for (let tick = 0; tick <= yMax + 1e-9; tick += step) yTicks.push(tick)

  // X ticks — one per calendar month spanned by the data.
  const monthTicks = timeMonth.range(timeMonth.floor(domainStart), timeMonth.offset(timeMonth.floor(domainEnd), 1))
  if (monthTicks.length === 0) monthTicks.push(timeMonth.floor(domainStart))
  // Thin labels when a long history would crowd the axis.
  const thin = Math.ceil(monthTicks.length / 12)
  const renderedTicks = monthTicks.filter((_, i) => i % thin === 0)
  const plotCenter = (MARGIN.left + (width - MARGIN.right)) / 2
  // A lone tick (data inside one month) sits under the data, not pinned to the Y axis.
  const tickX = (tick: Date) =>
    renderedTicks.length === 1 ? plotCenter : Math.max(MARGIN.left, Math.min(width - MARGIN.right, xScale(tick)))

  // Year handling: surface the year only when the data spans more than one calendar year,
  // labelling the first rendered tick and any tick whose year differs from the previous one
  // (robust to thinning). A single-year history shows months alone — the full date is in the
  // summary block above the chart.
  const spansMultipleYears = new Set(points.map(p => p.date.getFullYear())).size > 1
  const yearForTick = (tick: Date, index: number) =>
    spansMultipleYears && (index === 0 || tick.getFullYear() !== renderedTicks[index - 1].getFullYear())
      ? tick.getFullYear()
      : null

  const linePath =
    line<MetricPoint>()
      .x(p => xScale(p.date))
      .y(p => yScale(p.value))
      .curve(curveMonotoneX)(points) ?? ''

  const active = hovered !== null ? points[hovered] : null
  const activeX = active ? xScale(active.date) : 0
  const activeY = active ? yScale(active.value) : 0
  const tooltipLeft = Math.max(TOOLTIP_HALF_WIDTH, Math.min(width - TOOLTIP_HALF_WIDTH, activeX))
  const tooltipBelow = activeY < MARGIN.top + 48

  return (
    <div className={cn('w-full font-poppins', className)}>
      {title && <p className='mb-6 text-lg font-semibold text-custom-neutral-900'>{title}</p>}

      <div ref={containerRef} className='relative w-full'>
        <svg
          width={width}
          height={height}
          role='img'
          aria-label={`${title ? `${title}. ` : ''}${yAxisLabel} line chart`}
          className='overflow-visible'
        >
          {/* Horizontal gridlines + Y tick labels */}
          {yTicks.map(tick => (
            <g key={tick}>
              <line
                x1={MARGIN.left}
                x2={width - MARGIN.right}
                y1={yScale(tick)}
                y2={yScale(tick)}
                stroke='currentColor'
                className='text-custom-neutral-200'
                strokeWidth={1}
              />
              <text
                x={MARGIN.left - 12}
                y={yScale(tick)}
                textAnchor='end'
                dominantBaseline='middle'
                className='fill-custom-neutral-400 text-xs'
              >
                {tick}
              </text>
            </g>
          ))}

          {/* Rotated Y axis title */}
          <text
            transform={`translate(14 ${(MARGIN.top + height - MARGIN.bottom) / 2}) rotate(-90)`}
            textAnchor='middle'
            className='fill-custom-neutral-400 text-xs'
          >
            {yAxisLabel}
          </text>

          {/* X axis month (and year) labels */}
          {renderedTicks.map((tick, index) => {
            const year = yearForTick(tick, index)
            const x = tickX(tick)
            return (
              <g key={tick.getTime()}>
                <text
                  x={x}
                  y={height - MARGIN.bottom + 22}
                  textAnchor='middle'
                  className='fill-custom-neutral-400 text-xs'
                >
                  {format(tick, 'MMM')}
                </text>
                {year !== null && (
                  <text
                    x={x}
                    y={height - MARGIN.bottom + 36}
                    textAnchor='middle'
                    className='fill-custom-neutral-400 text-[10px]'
                  >
                    {year}
                  </text>
                )}
              </g>
            )
          })}

          {/* Trend line */}
          <path d={linePath} fill='none' stroke={color} strokeWidth={2} strokeLinecap='round' strokeLinejoin='round' />

          {/* Markers + hover hit areas */}
          {points.map((point, index) => (
            <g key={`${point.date.getTime()}-${index}`}>
              <circle
                cx={xScale(point.date)}
                cy={yScale(point.value)}
                r={5}
                fill='white'
                stroke={color}
                strokeWidth={1.5}
              />
              {/* biome-ignore lint/a11y/noStaticElementInteractions: enlarged transparent hit area for the data point's hover tooltip (data also exposed via the sr-only summary below) */}
              <circle
                cx={xScale(point.date)}
                cy={yScale(point.value)}
                r={14}
                fill='transparent'
                className='cursor-pointer'
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered(null)}
              />
            </g>
          ))}
        </svg>

        {active && (
          <div
            className={cn(
              'pointer-events-none absolute z-10 -translate-x-1/2 rounded-lg bg-custom-neutral-900 px-3 py-2 text-center shadow-lg',
              !tooltipBelow && '-translate-y-full'
            )}
            style={{ left: tooltipLeft, top: tooltipBelow ? activeY + 12 : activeY - 12 }}
          >
            <p className='whitespace-nowrap text-sm font-semibold text-white'>{formatValue(active.value)}</p>
            <p className='whitespace-nowrap text-xs text-white/70'>{format(active.date, 'MMM d, yyyy')}</p>
          </div>
        )}

        {/* Text alternative for assistive tech (the SVG itself only carries a label). */}
        <span className='sr-only'>
          {`${yAxisLabel} over time: ${points
            .map(p => `${formatValue(p.value)} on ${format(p.date, 'MMM d, yyyy')}`)
            .join('; ')}`}
        </span>
      </div>
    </div>
  )
}
